import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  CreateOrEditTemplate,
  DesktopTemplate,
  MobileTemplate,
  TemplatePreview,
} from "common/components/Templates";
import { Button } from "common/components/ui/Button";
import { TEMPLATE_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { ContentHeader, ContentScroll } from "common/layouts/WorkspaceLayout";
import type { Template, TemplateType } from "common/types";

import DiscardNewTemplateDialog from "./DiscardNewTemplateDialog";

export default function NewTemplate() {
  const isDesktop = useTwBreakpoint("lg");
  const navigate = useNavigate();
  const { search } = useLocation();
  const { accounts } = useSelectedWorkspaceContext();
  const { templateType } = useParams();

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template>();

  const newTemplateType = templateType.toUpperCase() as TemplateType;

  return (
    <>
      <CreateOrEditTemplate
        initialData={newTemplateType}
        onSuccess={({ id }) => navigate({ pathname: `../${id}`, search })}
      >
        {(onSubmit, isLoading, form) => {
          const updatedTemplate = form.watch();
          const previewComponent = (
            <TemplatePreview
              previewTemplate={previewTemplate}
              updatedTemplate={updatedTemplate}
              senderProfiles={accounts}
              onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
            />
          );
          const saveButton = (
            <Button onClick={onSubmit} isLoading={isLoading}>
              Save
            </Button>
          );
          return (
            <>
              {isDesktop ? (
                <>
                  <ContentHeader>
                    <h2 className="text-md-bold lg:text-lg-bold">
                      {`${TEMPLATE_TYPES[newTemplateType].shortLabel} template`}
                    </h2>
                  </ContentHeader>

                  <ContentScroll className="p-4 gap-y-4">
                    <DesktopTemplate
                      form={form}
                      previewComponent={previewComponent}
                      isCreatingNew
                    />
                  </ContentScroll>

                  {/* Footer */}
                  <div className="p-2.5 mt-auto border-t border-black-200 flex justify-between">
                    <Button
                      variant="secondary-black"
                      onClick={() => setIsDiscardOpen(true)}
                    >
                      Discard
                    </Button>
                    {saveButton}
                  </div>
                </>
              ) : (
                <MobileTemplate
                  form={form}
                  previewComponent={previewComponent}
                  updatePreview={() => setPreviewTemplate(updatedTemplate)}
                  saveButton={saveButton}
                  onClose={() => setIsDiscardOpen(true)}
                />
              )}
            </>
          );
        }}
      </CreateOrEditTemplate>

      <DiscardNewTemplateDialog
        isOpen={isDiscardOpen}
        setIsOpen={setIsDiscardOpen}
      />
    </>
  );
}
